import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { Checkbox, FormFlowPage, FrequencyDropdown, TextInput } from "src/components";
import { createStyles } from "src/styles";
import { DebtBalanceAndPaymentsValue } from "src/types/Application";
import { noInput } from "src/utilities/input";

import { enums, formatters } from "@fraction/shared";

const { DEBT_TYPE_MAP } = formatters.enums;

export interface DebtBalanceAndPaymentsProps {
  onChange: (value: DebtBalanceAndPaymentsValue) => void;
  value?: DebtBalanceAndPaymentsValue;
  lenderName: string;
  debtType?: enums.DebtType;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const styles = createStyles({
  checkbox: {
    marginBottom: 20,
  },
});

const DebtBalanceAndPayments = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  lenderName,
  debtType,
  ...props
}: DebtBalanceAndPaymentsProps) => {
  const validate = useCallback(
    () => value?.payOffEveryMonth || !(noInput(value?.balance) || noInput(value?.paymentSize)),
    [value]
  );
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });

  const handleNextFinal = useCallback(
    (event: React.ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();

      // If there is no frequency set, we default to monthly
      if (!value?.frequency) {
        onChange({
          ...value,
          frequency: enums.Frequency.MONTHLY,
        });
      }

      handleNext();
    },
    [onChange, value, handleNext]
  );

  const handleChangeBalance = useCallback(
    (balance?: string) => {
      onChange({
        ...value,
        balance,
      });
    },
    [onChange, value]
  );

  const handleChangePaymentSize = useCallback(
    (paymentSize?: string) => {
      onChange({
        ...value,
        paymentSize,
      });
    },
    [onChange, value]
  );

  const handleChangeFrequency = useCallback(
    (frequency?: enums.Frequency) => {
      onChange({
        ...value,
        frequency,
      });
    },
    [onChange, value]
  );

  const handlePayOffEveryMonth = useCallback(
    (payOffEveryMonth?: boolean) => {
      onChange({
        ...value,
        payOffEveryMonth,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title="Debt balance and payments"
      description={`How much do you owe ${lenderName} on your ${
        debtType ? DEBT_TYPE_MAP[debtType].toLowerCase() : "debt"
      } and how often do you pay it down?`}
      onNext={handleNextFinal}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      {debtType === enums.DebtType.CREDIT_CARD && (
        <Checkbox
          style={styles.checkbox}
          onChange={handlePayOffEveryMonth}
          label="I pay this off every month"
          checked={value?.payOffEveryMonth}
        />
      )}
      <TextInput
        ref={focusRef}
        name="balance"
        label="Debt balance"
        value={value?.balance || ""}
        onChange={handleChangeBalance}
        disabled={value?.payOffEveryMonth}
        min={0}
        max={50000000}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
      />
      <TextInput
        name="paymentSize"
        label="Payment amount"
        value={value?.paymentSize || ""}
        onChange={handleChangePaymentSize}
        disabled={value?.payOffEveryMonth}
        min={0}
        max={value?.balance || 1000000}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
      />
      <FrequencyDropdown
        label="Payment frequency"
        value={value?.frequency}
        onChange={handleChangeFrequency}
        disabled={value?.payOffEveryMonth}
      />
    </FormFlowPage>
  );
};

export default memo(DebtBalanceAndPayments);
